import { Link } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type { BadgeVariant } from '@/lib/status-tone';
import { formatDate } from '@/lib/format';
import { formatMinor } from '@/lib/money';
import type { FinanceQueueItem } from '@/api/generated/types.gen';
import type { PaymentScheduleEntry } from '@/lib/payments';

interface Props {
  items: readonly FinanceQueueItem[];
  /** finance.confirm — gates the selection column and every row action. */
  canConfirm: boolean;
  selected: ReadonlySet<string>;
  onToggle: (key: string) => void;
  onSettle: (item: FinanceQueueItem) => void;
  onSkip: (item: FinanceQueueItem) => void;
  onSplit: (item: FinanceQueueItem) => void;
  onMove: (item: FinanceQueueItem) => void;
  /** Open the booking card's ConfirmReceiptDialog for a 'schedule' row. */
  onConfirmSchedule: (item: FinanceQueueItem) => void;
  categoryNames: Record<string, string>;
  apartmentNames: Record<string, string>;
  emptyMessage?: string;
}

/**
 * The queue row's identity (spec §8.1's composite target, contract §10's
 * seam for slice 3). Exactly one of the four is set on every row THIS slice
 * can produce (source is always 'expected' — lib/finance.ts's
 * financeQueueQuery doc comment), so a row with none is not a row slice 2's
 * server can emit; failing loudly here beats two such rows silently
 * colliding under the same '' key in the `selected` Set and in React's own
 * reconciliation.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function queueItemKey(it: FinanceQueueItem): string {
  const id =
    it.target.expected_id ?? it.target.schedule_entry_id ?? it.target.payment_id ?? it.target.refund_id;
  if (!id) throw new Error('finance queue row has no target id (expected/schedule/payment/refund)');
  return id;
}

/**
 * A 'schedule' queue row as the PaymentScheduleEntry ConfirmReceiptDialog
 * takes. The dialog reads only id, method, amount_minor and currency; the
 * queue row carries all four (method travels as `origin` on this source), so
 * no extra fetch is needed. The remaining fields are filler for the type.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function scheduleEntryFromQueueItem(it: FinanceQueueItem): PaymentScheduleEntry {
  const id = it.target.schedule_entry_id;
  if (!id) throw new Error('queue row is not a schedule row');
  return {
    id,
    seq: 0,
    amount_minor: it.amount_minor,
    currency: it.currency,
    expected_at: it.due_date,
    method: it.origin as PaymentScheduleEntry['method'],
    status: 'expected',
    overdue: it.overdue,
    origin: 'system',
  };
}

// due-ness is a date-derived signal separate from the row's persisted status
// (status-tone.ts's FINANCE_EXPECTED_STATUS_TONE comment) — the queue is the
// one screen that renders it, from the overdue/today flags the server
// already computed in the business timezone (spec §8.1), not a client-side
// date comparison that would disagree with the server across a timezone.
function dueTone(it: FinanceQueueItem): { variant: BadgeVariant; label: string } {
  if (it.overdue) return { variant: 'danger', label: 'Overdue' };
  if (it.today) return { variant: 'warning', label: 'Today' };
  return { variant: 'neutral', label: 'Upcoming' };
}

/**
 * The confirmation queue (spec §8.1). Exported separately from the route
 * (finance.queue.tsx), same as MovementsTable is from finance.movements —
 * a pure props-in table is testable without a QueryClient or a router match.
 */
export function QueueTable({
  items,
  canConfirm,
  selected,
  onToggle,
  onSettle,
  onSkip,
  onSplit,
  onMove,
  onConfirmSchedule,
  categoryNames,
  apartmentNames,
  emptyMessage = 'Nothing awaiting confirmation.',
}: Props) {
  if (items.length === 0) {
    return (
      <p className="rounded-[var(--radius-card)] border border-dashed border-[var(--border-hairline)] bg-card p-8 text-center text-sm text-muted">
        {emptyMessage}
      </p>
    );
  }
  return (
    <div role="region" aria-label="Payments awaiting confirmation" tabIndex={0} className="overflow-x-auto rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <table className="w-full min-w-[680px] text-sm">
        <caption className="sr-only">Payments awaiting confirmation. Amounts show the remaining balance. Scroll horizontally on smaller screens.</caption>
        <thead>
          <tr className="border-b border-[var(--border-hairline)] bg-[var(--surface-sunken)] text-left text-xs font-semibold uppercase tracking-[0.06em] text-muted">
            {canConfirm && <th scope="col" className="px-3 py-3" aria-label="Select" />}
            <th scope="col" className="px-4 py-3">Due</th>
            <th scope="col" className="px-4 py-3">Payment</th>
            <th scope="col" className="px-4 py-3">Apartment</th>
            <th scope="col" className="px-4 py-3 text-right">Remaining</th>
            {canConfirm && <th scope="col" className="px-4 py-3" aria-label="Actions" />}
          </tr>
        </thead>
        <tbody>
          {items.map((it) => {
            const key = queueItemKey(it);
            const tone = dueTone(it);
            const remaining = it.amount_minor - it.settled_minor;
            // Ruling FR-20 item 4: a schedule refund row of an obligation the
            // provider has not executed yet stays listed (the money is still
            // owed back) but cannot be confirmed — the booking card offers no
            // Confirm receipt, a batch settle answers not_executed. A refund
            // row with no obligation at all (refund_status null) is not
            // waiting for anyone: it keeps its booking link, where it is
            // closed by Edit → waived/cancelled (docs/30 §17).
            const awaitingProvider = it.source === 'schedule' && it.origin === 'refund' && it.refund_status === 'open';
            // Re-check FR-20 N1: only a refund row of an EXECUTED obligation
            // can be batch-settled — without an obligation the server answers
            // not_a_target and refuses the whole batch.
            const batchable =
              (it.source === 'expected' || it.source === 'schedule') &&
              !(it.origin === 'refund' && it.source === 'schedule' && it.refund_status !== 'executed');
            return (
              <tr key={key} className={`border-b border-[var(--border-hairline)] last:border-0 ${selected.has(key) ? 'bg-[var(--ollio-navy-light)]' : 'hover:bg-[var(--surface-sunken)]'}`}>
                {canConfirm && (
                  <td className="px-3 py-2">
                    {/* Fix round 1, item 2 (Ruling T23-2): a 'schedule' row
                        is now batch-selectable too (spec example A — a
                        weekly Airbnb payout covering several bookings'
                        capture-projection rows) — BatchSettle.tsx builds a
                        schedule_entry_id target for these. A provider fact
                        still has no batch shape at all
                        (FinanceBatchTarget), so it keeps its own
                        single-row path in the actions column below. */}
                    {batchable && (
                      <Checkbox
                        checked={selected.has(key)}
                        onChange={() => onToggle(key)}
                        // D-M9 (final review): every row's checkbox used to
                        // get the SAME accessible name whenever two rows
                        // shared a category — a screen-reader user couldn't
                        // tell them apart. Due date (+ apartment, when the
                        // row has one — the closest thing to "which
                        // booking" this DTO carries; FinanceQueueItem has
                        // no guest name) disambiguates.
                        aria-label={`Select ${categoryNames[it.category_id] ?? it.category_id}, ${formatDate(it.due_date)}${
                          it.apartment_id ? `, ${apartmentNames[it.apartment_id] ?? it.apartment_id}` : ''
                        }`}
                      />
                    )}
                  </td>
                )}
                <td className="px-4 py-2 whitespace-nowrap">
                  <Badge variant={tone.variant}>{tone.label}</Badge>
                  <span className="mt-1 block text-xs tabular-nums text-muted">{formatDate(it.due_date)}</span>
                </td>
                <td className="px-4 py-2">
                  {it.target.expected_id ? (
                    <Link to="/finance/expected/$id" params={{ id: it.target.expected_id }} className="font-semibold text-primary hover:underline">
                      {categoryNames[it.category_id] ?? it.category_id}
                    </Link>
                  ) : <span className="font-semibold">{categoryNames[it.category_id] ?? it.category_id}</span>}
                  <div className="mt-1 text-xs text-muted">
                    {({ rule: 'Recurring rule', operator: 'Manual', submission: 'Staff submission', refund: 'Refund' } as Record<string, string>)[it.origin] ?? it.origin}
                    {it.booking_source ? ` · ${it.booking_source}` : ''}
                  </div>
                  {it.note && <div className="text-xs text-muted">{it.note}</div>}
                </td>
                <td className="px-4 py-2">{it.apartment_id ? (apartmentNames[it.apartment_id] ?? '—') : '—'}</td>
                <td className="px-4 py-3 text-right tabular-nums whitespace-nowrap">
                  <span className="font-semibold">{formatMinor(remaining, it.currency)}</span>
                  <span className="mt-1 block text-xs text-muted">{it.direction === 'in' ? 'Incoming' : 'Outgoing'}</span>
                  {it.settled_minor > 0 && <span className="mt-1 block text-xs text-muted">{formatMinor(it.settled_minor, it.currency)} confirmed</span>}
                </td>
                {canConfirm && it.source === 'expected' && (
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button size="sm" variant="primary" onClick={() => onSettle(it)}>
                        Confirm
                      </Button>
                      <details className="text-left">
                        <summary className="flex h-8 cursor-pointer items-center rounded-[var(--radius-control)] border border-border px-3 text-sm font-semibold text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">More</summary>
                        <div className="mt-2 flex flex-col items-stretch gap-1">
                          <Button size="sm" variant="ghost" onClick={() => onSplit(it)}>Split</Button>
                          <Button size="sm" variant="ghost" onClick={() => onMove(it)}>Move</Button>
                          <Button size="sm" variant="ghost" onClick={() => onSkip(it)}>Skip</Button>
                        </div>
                      </details>
                    </div>
                  </td>
                )}
                {canConfirm && it.source === 'schedule' && (
                  <td className="px-4 py-2 whitespace-nowrap text-right text-sm">
                    {awaitingProvider ? (
                      <span className="text-muted">Waiting for provider</span>
                    ) : it.booking_id && it.target.schedule_entry_id && batchable ? (
                      <Button size="sm" variant="primary" onClick={() => onConfirmSchedule(it)}>
                        Confirm
                      </Button>
                    ) : it.booking_id ? (
                      // A refund row with no executed obligation cannot be
                      // confirmed (server: not_a_target); it is closed on the
                      // booking card (Edit → waived/cancelled, docs/30 §17).
                      <Link to="/bookings/$id" params={{ id: it.booking_id }} className="text-primary hover:underline">
                        Open booking →
                      </Link>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                )}
                {canConfirm && it.source === 'provider' && (
                  <td className="px-4 py-2 whitespace-nowrap text-right text-sm">
                    <Link to="/finance/events" className="text-primary hover:underline">
                      Review in Reconciliation →
                    </Link>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
